import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import type { CalendarSyncPage, SyncFailingFeed } from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { Collapse } from '../../../../motion';
import { Banner, BannerActions } from '../../dashboard/dashboard-units-components/dashboard-units.styled';
import { countOf } from '../../../../function-utils/plural';
import { spacing } from '../../../../style-utils/theme';
import { listingSectionHref } from '../listing/stays-listing-shell';
import { providerName, whenText } from '../../../../utils/stays-sync';
import type { SyncPillTone } from '../../../../utils/stays-sync';

/**
 * CALENDAR SYNC BANNERS (plan S5, screens §4; K-62, K-63): at most two at
 * once, by priority — a failing calendar > changes to review > imported stays
 * with no guest name > a listing with no calendar connected.
 *   - listing scope (the listing's Calendar sync tab and its page): every kind;
 *   - building scope (the Stays page with a building chosen): failing,
 *     review, guest name.
 * "Not now" on the not-connected nudge hides it for this listing on this
 * device for 30 days (localStorage — a per-viewer convenience; a blocked
 * store just shows the banner again).
 */

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

const NUDGE_KEY = (listingId: string) => `lobbero.stays.syncNudge.${listingId}`;
const NUDGE_MS = 30 * 24 * 60 * 60 * 1000;

const nudgeHidden = (listingId: string, now: Date): boolean => {
  try {
    const until = Number(window.localStorage.getItem(NUDGE_KEY(listingId)));

    return Number.isFinite(until) && until > now.getTime();
  } catch {
    return false;
  }
};

const hideNudge = (listingId: string, now: Date): void => {
  try {
    window.localStorage.setItem(NUDGE_KEY(listingId), String(now.getTime() + NUDGE_MS));
  } catch {
    /* a blocked store: the banner simply comes back */
  }
};

interface BannerSpec {
  key: string;
  tone: SyncPillTone;
  text: string;
  action: React.ReactNode;
}

const failingText = (f: Pick<SyncFailingFeed, 'provider' | 'listing_name' | 'last_success_at'>, zone: string, now: Date): string => {
  const where = f.listing_name ? ` for ${f.listing_name}` : '';
  const since = f.last_success_at ? `has not synced since ${whenText(f.last_success_at, zone, now)}` : 'has not synced yet';

  return `The ${providerName(f.provider)} calendar${where} ${since}.`;
};

const reviewText = (n: number): string => `${countOf(n, 'calendar change')} ${n === 1 ? 'needs' : 'need'} your review.`;
const nameText = (n: number): string =>
  `${countOf(n, 'imported stay')} ${n === 1 ? 'needs' : 'need'} a guest name. The booking site's calendar does not share it.`;

export type SyncBannersProps =
  | {
      scope: 'listing';
      listingId: string;
      listingName: string;
      page: CalendarSyncPage;
      zone: string;
      now: Date;
      canManage: boolean;
    }
  | {
      scope: 'building';
      buildingId: number;
      pending: number;
      failing: SyncFailingFeed[];
      guestNameRequired: number;
      zone: string;
      now: Date;
    };

export const SyncBanners: React.FC<SyncBannersProps> = props => {
  const [nudgeOff, setNudgeOff] = useState(() => props.scope === 'listing' && nudgeHidden(props.listingId, props.now));
  const specs: BannerSpec[] = [];

  if (props.scope === 'listing') {
    const { page, listingId, listingName, zone, now, canManage } = props;
    const failing = page.feeds.find(f => f.health.state === 'failing');
    if (failing) {
      specs.push({
        key: 'failing',
        tone: 'alert',
        text: failingText({ provider: failing.provider, listing_name: listingName, last_success_at: failing.health.last_success_at }, zone, now),
        action: <Link to={listingSectionHref(listingId, 'sync')}>Open calendar sync</Link>,
      });
    }
    if (page.reviews.pending > 0) {
      specs.push({
        key: 'review',
        tone: 'brand',
        text: reviewText(page.reviews.pending),
        action: <Link to={`/dashboard/stays/sync-review?listing=${encodeURIComponent(listingId)}`}>Review changes</Link>,
      });
    }
    if (page.guest_name_required > 0) {
      specs.push({
        key: 'names',
        tone: 'warning',
        text: nameText(page.guest_name_required),
        action: <Link to={listingSectionHref(listingId, 'stays')}>Open the stays</Link>,
      });
    }
    if (canManage && !nudgeOff && page.listing_status === 'active' && page.feeds.length === 0 && page.imported_recent === 0) {
      specs.push({
        key: 'connect',
        tone: 'neutral',
        text: `Calendar sync is not connected for ${listingName}. New stays from booking sites cannot arrive until a calendar link is saved.`,
        action: (
          <>
            <Link to={listingSectionHref(listingId, 'sync')}>Connect a calendar</Link>
            <AppButton
              variant="ghost"
              size="compact"
              type="button"
              aria-label={`Not now: hide the calendar sync reminder for ${listingName}`}
              onClick={() => {
                hideNudge(listingId, now);
                setNudgeOff(true);
              }}
            >
              Not now
            </AppButton>
          </>
        ),
      });
    }
  } else {
    const { failing, pending, guestNameRequired, buildingId, zone, now } = props;
    if (failing.length) {
      const f = failing[0];
      const more = failing.length > 1 ? ` ${countOf(failing.length - 1, 'other calendar')} also ${failing.length - 1 === 1 ? 'fails' : 'fail'}.` : '';
      specs.push({
        key: 'failing',
        tone: 'alert',
        text: `${failingText(f, zone, now)}${more}`,
        action: <Link to={listingSectionHref(f.listing_id, 'sync')}>Open calendar sync</Link>,
      });
    }
    if (pending > 0) {
      specs.push({
        key: 'review',
        tone: 'brand',
        text: reviewText(pending),
        action: <Link to={`/dashboard/stays/sync-review?building=${encodeURIComponent(String(buildingId))}`}>Review changes</Link>,
      });
    }
    if (guestNameRequired > 0) {
      specs.push({ key: 'names', tone: 'warning', text: nameText(guestNameRequired), action: null });
    }
  }

  const shown = specs.slice(0, 2);
  const keys = new Set(shown.map(s => s.key));

  return (
    <Stack data-testid="sync-banners">
      {['failing', 'review', 'names', 'connect'].map(key => {
        const spec = specs.find(s => s.key === key);

        return (
          <Collapse key={key} open={keys.has(key)}>
            {spec && keys.has(key) ? (
              <Banner tone={spec.tone} role={spec.tone === 'alert' ? 'alert' : 'status'} data-testid={`sync-banner-${key}`}>
                <div>
                  <div>{spec.text}</div>
                  {spec.action ? <BannerActions>{spec.action}</BannerActions> : null}
                </div>
              </Banner>
            ) : null}
          </Collapse>
        );
      })}
    </Stack>
  );
};
